import React, { useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Formik, ErrorMessage } from 'formik';

import './styles.css';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicInput from '../../../../components/BasicInput';
import BasicSlideToggle from '../../../../components/BasicSlideToggle';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import HorizontalLoader from '../../../../components/HorizontalLoader';
import LoadMask from '../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import useApi from '../../../../hooks/useApi';
import {useFiltersAccordion} from '../../../../hooks/useFiltersAccordion';
import {TYPE} from '../../../../pages/SetupEmail';
import FiltersAccordion from '../../../FiltersAccordion';
import FiltersPartial from '../../../FiltersAccordion/components/FiltersPartial';
import {Normalizer} from './normalizer';
import {AddSetupEmailRequirementsFormValidationSchema} from './validate';

const AddSetupEmailRequirementsForm = props => {
  const {
    actions: { handleError, proceed, dismiss },
    params: { id, type, reqId = null },
  } = props;

  const api = useApi();

  const [formData, setFormData] = useState({
    init: false,
    fields: {
      id,
      reqId,
      Name: '',
      Active: 'No',
    },
    filters: {},
  });

  const [
    { sections, query },
    {
      init: initFilters,
      clear,
      change,
      groupClear,
      groupSelect,
      push,
      search: filterSearch,
    },
  ] = useFiltersAccordion();

  useEffect(() => {
    const params = reqId ? { id, reqId } : { id };

    api.get(
      TYPE.PROCESS === type
        ? ENDPOINT.FORM.GET.JOB_EMAIL_SETUP_REQ
        : ENDPOINT.FORM.GET.PROJECT_EMAIL_SETUP_REQ,
      params,
      {
        handleError: response => handleError(response),
        handleSuccess: response => {
          setFormData(formData =>
            Normalizer.fetch({
              ...response,
              init: true,
              fields: { ...formData.fields, ...response.fields },
            })
          );
          initFilters(response.filters);
        },
      }
    );
  }, []);

  const handleSubmit = (
    values,
    { setSubmitting, setErrors, setFieldError }
  ) => {
    if (!Object.keys(query).length) {
      setFieldError('Value', 'Please add at least 1 filter');
      setSubmitting(false);
      return;
    }

    const params = Normalizer.send(values);
    api.post(
      TYPE.PROCESS === type
        ? ENDPOINT.FORM.SAVE.JOB_EMAIL_SETUP_REQ
        : ENDPOINT.FORM.SAVE.PROJECT_EMAIL_SETUP_REQ,
      { ...params, ...query },
      {
        handleError: response => {
          setSubmitting(false);
          handleError(response, setErrors);
        },
        handleSuccess: response => proceed(response),
      }
    );
  };

  return formData.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={formData.fields}
          validationSchema={AddSetupEmailRequirementsFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            dirty,
            handleChange,
            handleBlur,
            handleSubmit,
            handleReset,
            isSubmitting,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit} className="form__content">
              {isSubmitting && <LoadMask />}

              <div
                className="form__header"
                style={{
                  display: 'flex',
                  padding: '0',
                  alignItems: 'center',
                  marginBottom: '25px',
                }}
              >
                <h4 className="setup-email__form-title">Email Requirements</h4>
                <div className="setup-email__toggle">
                  <BasicSlideToggle
                    name="Active"
                    label={values.Active ? 'Active' : 'Inactive'}
                    checked={values.Active}
                    onChange={setFieldValue}
                    disabled={false}
                  />
                </div>
              </div>

              <div
                className="form__group form__group--compact"
                style={{ maxWidth: '100%', marginBottom: '15px' }}
              >
                <BasicInput
                  label="title"
                  required={true}
                  name="Name"
                  inputType="text"
                  content={values.Name}
                  placeholder="Name..."
                  controlFunc={handleChange}
                  isValid={!(errors.Name && !touched.Name) || !touched.Name}
                  touchFunc={handleBlur}
                  labelStyles={{ fontWeight: 'bold' }}
                />

                <ErrorMessage
                  name="Name"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <FiltersAccordion
                title="Requirements"
                subTitle="Use the search filters below to narrow your confidential search."
                filters={sections}
                isFetching={false}
                onChange={change}
                onGroupSelectAll={groupSelect}
                onGroupClearAll={groupClear}
                onClearAll={clear}
                onSearch={filterSearch}
                onPushValue={push}
                compact={true}
                render={renderProps => (
                  <div style={{ position: 'relative' }}>
                    <FiltersPartial compact={true} {...renderProps} />
                    {errors.Value && (
                      <div
                        className="form__error form__error--custom"
                        style={{ position: 'absolute', bottom: '70px' }}
                      >
                        Please add at least 1 filter.
                      </div>
                    )}
                  </div>
                )}
              />

              <div
                className="form__controls form__controls--compact"
                style={{ maxWidth: '100%' }}
              >
                <BasicButtonGroup>
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Save"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <HorizontalLoader />
      </div>
    </div>
  );
};

export default AddSetupEmailRequirementsForm;
